import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { format } from "date-fns";
import { insertFmcsaIncidentSchema, type Company, type WorkOrder, type FmcsaIncidentWithRelations } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const editIncidentSchema = insertFmcsaIncidentSchema.partial();

type EditIncidentFormData = z.infer<typeof editIncidentSchema>;

interface EditFmcsaIncidentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incident: FmcsaIncidentWithRelations;
}

export function EditFmcsaIncidentModal({
  open,
  onOpenChange,
  incident,
}: EditFmcsaIncidentModalProps) {
  const [selectedWorkOrders, setSelectedWorkOrders] = useState<number[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<EditIncidentFormData>({
    resolver: zodResolver(editIncidentSchema),
    defaultValues: {
      incidentNumber: incident.incidentNumber,
      status: incident.status,
      notes: incident.notes || "",
      companyId: incident.companyId || undefined,
      expedited: incident.expedited ?? false,
    },
  });

  const expeditedValue = form.watch("expedited");

  // Set initial work orders when modal opens
  useEffect(() => {
    if (open && incident.workOrders) {
      setSelectedWorkOrders(incident.workOrders.map(wo => wo.id));
    }
  }, [open, incident.workOrders]);

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies", { context: "select" }],
    queryFn: async () => {
      const res = await fetch("/api/companies?limit=200", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: workOrders } = useQuery<any[]>({
    queryKey: ["/api/work-orders/list", { context: "select" }],
    queryFn: async () => {
      const res = await fetch("/api/work-orders/list?limit=200", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch work orders");
      return res.json();
    },
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const selectedCompanyId = form.watch("companyId");
  
  // Filter work orders by selected company
  const filteredWorkOrders = workOrders?.filter(wo => 
    selectedCompanyId ? wo.serviceBureauCompanyId === selectedCompanyId : true
  ) || [];

  const updateMutation = useMutation({
    mutationFn: (data: EditIncidentFormData) =>
      apiRequest("PUT", `/api/fmcsa-incidents/${incident.id}`, data),
    onSuccess: async () => {
      // Update work order links separately
      await apiRequest("POST", `/api/fmcsa-incidents/${incident.id}/link-work-orders`, { workOrderIds: selectedWorkOrders });
      
      await queryClient.invalidateQueries({ queryKey: ["/api/fmcsa-incidents"] });
      toast({
        title: "Success",
        description: "FMCSA incident updated successfully",
      });
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update FMCSA incident",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: EditIncidentFormData) => {
    updateMutation.mutate(data);
  };

  const handleWorkOrderToggle = (workOrderId: number) => {
    setSelectedWorkOrders(prev => 
      prev.includes(workOrderId) 
        ? prev.filter(id => id !== workOrderId)
        : [...prev, workOrderId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit FMCSA Incident</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="incidentNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Incident Number *</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Enter FMCSA incident number (e.g., 250708-001027)"
                        required
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Unresolved">Unresolved</SelectItem>
                        <SelectItem value="Resolved">Resolved</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="companyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(value ? parseInt(value) : undefined);
                      // Clear selected work orders when company changes
                      setSelectedWorkOrders([]);
                    }} 
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies?.map((company) => (
                        <SelectItem key={company.id} value={company.id.toString()}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ""}
                      placeholder="Enter incident details..."
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormField
                control={form.control}
                name="expedited"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        id="edit-expedited"
                      />
                    </FormControl>
                    <FormLabel htmlFor="edit-expedited" className="font-medium cursor-pointer">
                      Expedited
                    </FormLabel>
                  </FormItem>
                )}
              />
              {expeditedValue && incident.expeditedAt && (
                <p className="text-xs text-muted-foreground pl-7">
                  Set{incident.expeditedByUser
                    ? ` by ${incident.expeditedByUser.firstName} ${incident.expeditedByUser.lastName}`
                    : ""
                  } on {format(new Date(incident.expeditedAt), "MMM d, yyyy 'at' h:mm a")}
                </p>
              )}
            </div>

            {/* Work Orders Selection */}
            {filteredWorkOrders.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Link Work Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filteredWorkOrders.map((workOrder) => (
                      <div key={workOrder.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`work-order-${workOrder.id}`}
                          checked={selectedWorkOrders.includes(workOrder.id)}
                          onCheckedChange={() => handleWorkOrderToggle(workOrder.id)}
                        />
                        <label
                          htmlFor={`work-order-${workOrder.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {workOrder.workOrderNumber} - {workOrder.serviceType}
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Updating..." : "Update Incident"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}